import PropTypes from 'prop-types'
import React, { useEffect } from 'react'

import PaymentSeparator from 'components/PaymentSeparator'
import Icon from 'components/Icon'
import { MenuMainContext } from 'contexts/MenuMain'
import { ResizeContext } from 'contexts/Resize'
import OrderPersInfoRuForm from 'forms/OrderPersInfoRuForm'
import { useResetCollectDataStep } from 'hooks/cart/collectDataStep'
import { useDropdownOptions } from 'hooks/cart/useDropdownOptions'
import { useInitialPersonalInfoValues } from 'hooks/cart/useInitialPersonalInfoValues'
import { usePersonalInfoForm } from 'hooks/cart/usePersonalnfoForm'
import { gtmCheckoutCardInfo, gtmEvent } from 'utils/client/analitics'

import ApplePayPayment from './components/ApplePayPayment'
import { version } from './constants/applePay'
import {
  ButtonOrderBox,
  OfferErrorBox,
  HintIconWrapper,
  ScrollToMeStyled,
} from './styles'
import getCartOptions from './utils/getCartOptions'
import ButtonAsyncReclickable from 'components/ButtonAsyncReclickable'

const PERSONAL_INFO_FORM_ID = 'form-personal-info'

/**
 * Component of CartFormRussia
 *
 * Here we got form for Russian region and some stuff to work with Yandex
 * We are verify value of agreement checkbox only inside this component
 */
const CartFormRussia = ({
  dict,
  texts,
  locale,
  cart,
  countries,
  sales,

  personalInfoChange,
  handleFailure,
  handleSuccess,

  handleCollect,
  handleCollectReset,
  handlePayment,
  hideCheckoutButton,
  registerCheckoutHandler,
  registerOfferError,
}) => {
  const {
    isOrderSucceed,
    isFetching,
    deliveryType,
    isOrderCollected,
    error,
  } = cart
  const { menuNode } = React.useContext(MenuMainContext)
  const { mobile } = React.useContext(ResizeContext)
  const piFormRef = React.useRef(null)
  const cartOptions = React.useMemo(
    () => getCartOptions(cart, dict, texts, sales),
    [cart, dict, sales, texts],
  )

  /*
   * Options for dropdowns
   */
  const {
    defaultCountryValue,
    defaultPhoneCodeValue,
    optionsCountry,
    optionsPhoneCode,
  } = useDropdownOptions({ countries, dict, locale })

  /*
   * Object with initial values for form
   * > be careful to change this object it will cause to form reload (loop danger)
   */
  const personalInfoInitialValues = useInitialPersonalInfoValues({
    personal: cart.personal,
    defaultCountryValue,
    defaultPhoneCodeValue,
    deliveryType: 'yandex',
    payType: 'card',
  })
  const [payType, setPayType] = React.useState(
    personalInfoInitialValues.payType,
  )

  /*
   * Personal Info form
   * > save to state validity status for form
   * > function to handle changes
   */
  const [
    isPersonalInfoReady,
    errors,
    handlePersonalInfoChange,
  ] = usePersonalInfoForm({
    personalInfoChange,
  })

  // TODO: make it false after stock refill
  const temporarilyOutOfStock = true

  const handlePersonalInfoChangeWithPayType = React.useCallback(
    ({ values, invalid, active, touched, errors }) => {
      handlePersonalInfoChange({ values, invalid, active, touched, errors })
      if (values.payType !== payType) setPayType(values.payType)
    },
    [handlePersonalInfoChange, payType],
  )
  /*
   * remote personal info form submit
   */
  const remotePersonalInfoSubmit = React.useCallback(() => {
    const formNode = document.getElementById(PERSONAL_INFO_FORM_ID)

    if (!formNode) return

    if (typeof formNode.requestSubmit === 'function') {
      formNode.requestSubmit()
      return
    }

    const submitControl = formNode.querySelector(
      'input[name="submit"][type="submit"]',
    )

    if (submitControl) {
      submitControl.click()
      return
    }

    formNode.dispatchEvent(
      new Event('submit', { bubbles: true, cancelable: true }),
    )
  }, [])

  /*
   * Work with yandex card
   */
  const [, setIsCardReady] = React.useState(false)

  const getMessageWhenCreditCardReady = React.useCallback(
    event => {
      const isMessageFromCard = /(cardIsReady|cardIsFilling|cardError)/

      if (
        typeof event.data === 'string' &&
        isMessageFromCard.test(event.data)
      ) {
        const { cardIsReady, cardIsFilling, cardError, target } = JSON.parse(
          event.data,
        )

        if (cardIsReady) {
          handleCardInfoCompleteChange({ complete: cardIsReady })
        }

        if (cardIsFilling) {
          if (!target) {
            gtmCheckoutCardInfo({
              cart,
              salesConfig: sales,
            })
          } else {
            gtmEvent({ event: `Start fill - ${target}` })
          }
        }

        if (cardError) {
          gtmEvent({ event: `Card error - ${JSON.stringify(target)}` })
        }
      }
    },
    [cart, sales],
  )

  React.useEffect(() => {
    window.addEventListener('message', getMessageWhenCreditCardReady)

    return () => {
      window.removeEventListener('message', getMessageWhenCreditCardReady)
    }
  }, [getMessageWhenCreditCardReady])

  const handleCardInfoCompleteChange = ({ complete }) => {
    setIsCardReady(complete)
  }

  /*
   * Check apple pay support
   */
  const [isApplePaySupport, setApplePaySupport] = React.useState(false)
  const checkApplePaySupport = React.useCallback(() => {
    const merchantId = sales?._yandexMerchantId

    if (
      merchantId &&
      window.location.protocol === 'https:' &&
      window.ApplePaySession
    ) {
      const promise = window.ApplePaySession.canMakePaymentsWithActiveCard(
        merchantId,
      )
      promise.then(function(canMakePayments) {
        if (
          canMakePayments &&
          window.ApplePaySession.supportsVersion(version)
        ) {
          setApplePaySupport(true)
        }
      })
    }
  }, [sales])

  React.useEffect(() => {
    checkApplePaySupport()
  }, [checkApplePaySupport])

  React.useEffect(() => {
    if (isOrderCollected) {
      gtmEvent({ event: 'Bank Card - Showed' })
    }
  }, [isOrderCollected])

  // Since apple pay in Russia does not support subscriptions, we must hide the apple pay input
  const isBiomeSubscription =
    cart.goods.biome.isSubscription && !!cart.goods.biome.amount
  // Temporary turned off ApplePay for Russia
  const showApplePayButton = false && isApplePaySupport && !isBiomeSubscription

  /*
   * Callback
   * > Order info Collect handler
   */
  const handleCollectFromValidSubmit = React.useCallback(() => {
    if (!isOrderCollected && !isFetching) {
      handleCollect()
      gtmEvent({ event: 'Click Button Next - Success' })
    }
  }, [handleCollect, isFetching, isOrderCollected])

  /*
   * Callback
   * > Order info Reset handler
   */
  useResetCollectDataStep({
    menuNode,
    piFormRef,
    handleCollectReset,
  })

  /*
   * Callback
   * > Payment handler
   */
  const [isOfferHintVisible, setOfferHintVisibility] = React.useState(false)

  const [shouldProceedToPayment, setShouldProceedToPayment] = React.useState(
    false,
  )

  const handleButtonClick = React.useCallback(() => {
    setOfferHintVisibility(true)
    setShouldProceedToPayment(true)

    if (!isPersonalInfoReady) {
      gtmEvent({
        event: 'Click Button Next - Error',
        description: errors ? JSON.stringify(errors) : 'all',
      })
    }

    remotePersonalInfoSubmit()
  }, [errors, isPersonalInfoReady, remotePersonalInfoSubmit])

  React.useEffect(() => {
    if (!registerCheckoutHandler) return undefined

    registerCheckoutHandler(() => handleButtonClick)

    return () => {
      registerCheckoutHandler(null)
    }
  }, [handleButtonClick, registerCheckoutHandler])

  React.useEffect(() => {
    if (registerOfferError) {
      registerOfferError(
        errors.is_offer_accepted && isOfferHintVisible
          ? texts.form.fields.agreement.offer_error
          : null,
      )
    }
  }, [
    errors.is_offer_accepted,
    isOfferHintVisible,
    registerOfferError,
    texts.form.fields.agreement.offer_error,
  ])

  useEffect(() => {
    if (
      shouldProceedToPayment &&
      isOrderCollected &&
      Object.keys(errors).length === 0
    ) {
      handlePayment()
      setShouldProceedToPayment(false)
    }
  }, [shouldProceedToPayment, isOrderCollected, errors, handlePayment])

  return (
    <>
      <OrderPersInfoRuForm
        key={`info-${locale}`}
        ref={piFormRef}
        texts={texts.form}
        id={PERSONAL_INFO_FORM_ID}
        onChange={handlePersonalInfoChangeWithPayType}
        onSubmit={handleCollectFromValidSubmit}
        errorsDict={dict.errors}
        optionsCountry={optionsCountry}
        optionsPhoneCode={optionsPhoneCode}
        initialValues={personalInfoInitialValues}
        isDisabled={isFetching}
        deliveryType={deliveryType}
      />
      {!hideCheckoutButton && (
        <>
          {temporarilyOutOfStock ? (
            <div>{dict.info.temporarily_out_of_stock_description}</div>
          ) : (
            <ButtonOrderBox>
              <div className="btn-rus-wrapper">
                <ButtonAsyncReclickable
                  onClick={handleButtonClick}
                  isLoading={isFetching}
                  isMobile={mobile}
                >
                  {dict.buttons.checkout}
                </ButtonAsyncReclickable>
              </div>
              {errors.is_offer_accepted && isOfferHintVisible && (
                <OfferErrorBox>
                  <HintIconWrapper>
                    <Icon id="btn_info" color="pink" />
                  </HintIconWrapper>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: texts.form.fields.agreement.offer_error,
                    }}
                  />
                </OfferErrorBox>
              )}
            </ButtonOrderBox>
          )}
        </>
      )}
      {isOrderCollected && (
        <>
          {showApplePayButton && (
            <>
              <ScrollToMeStyled {...{ menuNode }} />
              <PaymentSeparator texts={texts} isBefore={true}>
                <ApplePayPayment
                  isSucceed={!!isOrderSucceed}
                  isError={!!error}
                  handlePayment={handlePayment}
                  handleSuccess={handleSuccess}
                  handleFailure={handleFailure}
                  cartOptions={cartOptions}
                />
              </PaymentSeparator>
            </>
          )}
        </>
      )}
    </>
  )
}

CartFormRussia.propTypes = {
  cart: PropTypes.object.isRequired,
  countries: PropTypes.array.isRequired,
  dict: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  texts: PropTypes.object.isRequired,
  personalInfoChange: PropTypes.func.isRequired,
  handleFailure: PropTypes.func.isRequired,
  handleSuccess: PropTypes.func.isRequired,
  handleCollect: PropTypes.func.isRequired,
  handleCollectReset: PropTypes.func.isRequired,
  handlePayment: PropTypes.func.isRequired,
  sales: PropTypes.object.isRequired,
  hideCheckoutButton: PropTypes.bool,
  registerCheckoutHandler: PropTypes.func,
  registerOfferError: PropTypes.func,
}

CartFormRussia.defaultProps = {
  hideCheckoutButton: false,
  registerCheckoutHandler: null,
  registerOfferError: null,
}

export default CartFormRussia
